import { useCallback } from "react";
import useAppContext from "./useAppContext";
import { CommonCalls } from "../../config/calls";

/**
 * Custom hook for managing user metadata updates
 * Provides functionality to update user metadata via the updateMetadata endpoint
 * @returns {Object} Object containing updateMetadata function
 */
function useUserMetadata() {
  const appContext = useAppContext();

  /**
   * Compares two metadata objects to check if they differ
   * Handles null/undefined/empty string differences appropriately
   * @param {Object} currentMetadata - Current metadata object
   * @param {Object} newMetadata - New metadata object to compare
   * @returns {boolean} True if metadata differs, false otherwise
   */
  function hasMetadataChanged(currentMetadata, newMetadata) {
    // If current metadata doesn't exist, any new metadata is a change
    if (!currentMetadata || Object.keys(currentMetadata).length === 0) {
      return Object.keys(newMetadata).length > 0;
    }

    // Compare each field in newMetadata
    for (const key in newMetadata) {
      const currentValue = currentMetadata[key];
      const newValue = newMetadata[key];

      // Normalize values: treat null, undefined, and empty string as equivalent
      const normalizedCurrent =
        currentValue === null || currentValue === undefined || currentValue === "" ? null : currentValue;
      const normalizedNew = newValue === null || newValue === undefined || newValue === "" ? null : newValue;

      // If values differ, metadata has changed
      if (normalizedCurrent !== normalizedNew) {
        return true;
      }
    }

    // Check if any fields were removed (exist in current but not in new)
    for (const key in currentMetadata) {
      if (!(key in newMetadata)) {
        // Field was removed, but we only care about fields being updated
        // So we don't consider removal as a change for update purposes
        continue;
      }
    }

    return false;
  }

  /**
   * Updates user metadata via the updateMetadata endpoint
   * @param {Object} newMetadata - Metadata object to update (already extracted by RegistrationMetadataUtils)
   * @param {Object} opts - Options object
   * @param {boolean} opts.force - If true, skip comparison and always update; if false or undefined, compare first
   * @returns {Promise<void>} Promise that resolves when update is complete
   */
  const updateMetadata = useCallback(
    async (newMetadata, opts = {}) => {
      const { force = false } = opts;

      // Get current metadata from appContext
      const userId = appContext?.data?.user?._id;
      const currentMetadata = appContext?.data?.user?.metadata || {};

      // Merge current metadata with new metadata to preserve existing fields
      const mergedMetadata = { ...currentMetadata, ...newMetadata };

      // If not forcing, check if metadata has changed
      if (!force) {
        if (!hasMetadataChanged(currentMetadata, newMetadata)) {
          // No changes detected, skip update
          return;
        }
      }

      try {
        // Call the updateMetadata endpoint with merged metadata
        const response = await CommonCalls.updateMetadata({ userId, metadata: mergedMetadata });

        // Update appContext with the returned user object
        // The response contains the full updated user object in response.data.user
        if (response?.data && appContext?.updateUserData) {
          appContext.updateUserData({ user: response.data });
        }
      } catch (error) {
        // Handle errors gracefully - log but don't throw
        console.error("Failed to update user metadata", error);
      }
    },
    [appContext],
  );

  return {
    updateMetadata,
  };
}

export default useUserMetadata;
